// English placeholder values for date and times, without an LLM.
// Mirror of backend/src/edicta/mail/en_values.py (the backend renders the
// mail with the same rules; tests live there) — keep both in sync.

const AUTO_KEYS = ["date", "start", "end", "building"];

// Group phrases the backend puts into {{building}} for many or all dorms.
const BUILDING_GROUPS: Record<string, string> = {
  "allen wohnheimen": "all dormitories",
  "mehreren wohnheimen": "several dormitories",
};

const WEEKDAYS: Record<string, string> = {
  montag: "Monday",
  mo: "Monday",
  dienstag: "Tuesday",
  di: "Tuesday",
  mittwoch: "Wednesday",
  mi: "Wednesday",
  donnerstag: "Thursday",
  do: "Thursday",
  freitag: "Friday",
  fr: "Friday",
  samstag: "Saturday",
  sa: "Saturday",
  sonntag: "Sunday",
  so: "Sunday",
};
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_NAMES: Record<string, number> = {
  januar: 1,
  jan: 1,
  februar: 2,
  feb: 2,
  märz: 3,
  maerz: 3,
  mär: 3,
  mrz: 3,
  april: 4,
  apr: 4,
  mai: 5,
  juni: 6,
  jun: 6,
  juli: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  oktober: 10,
  okt: 10,
  november: 11,
  nov: 11,
  dezember: 12,
  dez: 12,
};

const WEEKDAY = String.raw`(?:(?<wd>[A-Za-zÄÖÜäöü]+)\.?,?\s+(?:den\s+)?)?`;
const NUMERIC_DATE = new RegExp(
  String.raw`^${WEEKDAY}(?<d>\d{1,2})\.\s*(?<m>\d{1,2})\.(?:\s*(?<y>\d{4}|\d{2}))?$`,
);
const NAMED_DATE = new RegExp(
  String.raw`^${WEEKDAY}(?<d>\d{1,2})\.\s*(?<mn>[A-Za-zÄÖÜäöü]+)\.?(?:\s+(?<y>\d{4}))?$`,
);
const TIME_VALUE = new RegExp(
  String.raw`^(?<pre>ca\.|circa|etwa|gegen|ab|bis|um)?\s*(?<h1>\d{1,2})(?:[:.](?<m1>\d{2}))?` +
    String.raw`(?:\s*(?:-|–|bis)\s*(?<h2>\d{1,2})(?:[:.](?<m2>\d{2}))?)?\s*(?:Uhr)?$`,
  "i",
);
const PREFIX_EN: Record<string, string> = {
  "ca.": "approx.",
  circa: "approx.",
  etwa: "approx.",
  gegen: "around",
  ab: "from",
  bis: "until",
  um: "at",
};

function squash(value: string): string {
  return value.split(/\s+/).filter(Boolean).join(" ");
}

/** Day, month, optional year and English weekday of a German date value, or null. */
export function parseGermanDate(
  value: string,
): { day: number; month: number; year?: number; weekday?: string } | null {
  const text = squash(value ?? "");
  let match = NUMERIC_DATE.exec(text);
  let month: number | undefined;
  if (match?.groups) {
    month = Number(match.groups.m);
  } else {
    match = NAMED_DATE.exec(text);
    if (match?.groups) month = MONTH_NAMES[match.groups.mn.toLowerCase()];
  }
  if (!match?.groups || month === undefined || month < 1 || month > 12)
    return null;
  const day = Number(match.groups.d);
  if (day < 1 || day > 31) return null;
  let weekday: string | undefined;
  if (match.groups.wd) {
    weekday = WEEKDAYS[match.groups.wd.toLowerCase()];
    if (!weekday) return null;
  }
  let year = match.groups.y;
  if (year && year.length === 2) year = `20${year}`;
  return { day, month, year: year ? Number(year) : undefined, weekday };
}

export function englishDate(value: string): string | null {
  const parsed = parseGermanDate(value);
  if (!parsed) return null;
  const { day, month, year, weekday } = parsed;
  const date = `${day} ${MONTHS[month - 1]}${year ? ` ${year}` : ""}`;
  return weekday ? `${weekday}, ${date}` : date;
}

function to12h(hour: number, minute: number): string {
  if (hour === 12 && minute === 0) return "12 p.m. (noon)";
  if (hour === 0 && minute === 0) return "12 a.m. (midnight)";
  const h12 = hour % 12 || 12;
  const suffix = hour < 12 ? "a.m." : "p.m.";
  return minute
    ? `${h12}:${String(minute).padStart(2, "0")} ${suffix}`
    : `${h12} ${suffix}`;
}

function clock(hour: string, minute: string | undefined): string | null {
  let h = Number(hour);
  const m = Number(minute ?? 0);
  if (h === 24 && m === 0) h = 0;
  if (h > 23 || m > 59) return null;
  return to12h(h, m);
}

export function englishTime(value: string): string | null {
  const match = TIME_VALUE.exec(squash(value ?? ""));
  if (!match?.groups) return null;
  const first = clock(match.groups.h1, match.groups.m1);
  if (!first) return null;
  let result = first;
  if (match.groups.h2) {
    const second = clock(match.groups.h2, match.groups.m2);
    if (!second) return null;
    result = `${first}–${second}`;
  }
  const prefix = match.groups.pre;
  return prefix ? `${PREFIX_EN[prefix.toLowerCase()]} ${result}` : result;
}

export function englishValue(key: string, value: string): string | null {
  if (!value?.trim()) return null;
  if (key === "date") return englishDate(value);
  if (key === "start" || key === "end") return englishTime(value);
  if (key === "building") return BUILDING_GROUPS[value.trim().split(/\s+/).join(" ").toLowerCase()] ?? null;
  return null;
}

/** German values, then automatic English date/times, then explicit English values. */
export function englishValues(
  placeholders: Record<string, string>,
  overrides: Record<string, string> = {},
): Record<string, string> {
  const values = { ...placeholders };
  for (const key of AUTO_KEYS) {
    const auto = englishValue(key, values[key] ?? "");
    if (auto) values[key] = auto;
  }
  for (const [key, value] of Object.entries(overrides)) {
    if (value?.trim()) values[key] = value;
  }
  return values;
}
